import dayjs from 'dayjs';
import { call, CallEffect, put, PutEffect, takeEvery } from 'redux-saga/effects';
import { ConsentActionType, GetUserConsentForClient, getUserConsentForClient } from '../actions/consentActions';
import { LoginAndRegisterActionType } from '../actions/loginAndRegisterActions';
import { RequestActionType, requestError, requestSuccess } from '../actions/requestActions';
import { checkTermsAndConditions, setTermsAndConditions, tokenExchange } from '../api/sso';
import { LoginResponse, SsoAuthorizationState, SsoQueryParams, TAndCResponse } from '../models/commonModels';
import RequestActions from '../models/RequestTypes';
import { RequestType } from '../reducers/requestReducer';
import { toErrorType } from './isErrorType';
import { redirectWithQuery, shouldRedirectToClient, updateTokenStorage } from './loginAndRegister/loginSaga';

const getSearchQueryFromState = (): SsoQueryParams => {
    const searchQuery = window.location.search;
    const hashes = searchQuery.slice(searchQuery.indexOf('?') + 1).split('&');
    const redirectAttrs = hashes.reduce((redirectInfo, hash) => {
        const attr = hash.split('=');
        return { ...redirectInfo, [attr[0]]: attr[1] };
    }, {});

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const parsedState: SsoAuthorizationState = JSON.parse(window.atob(decodeURIComponent(redirectAttrs.state)));
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const authCode: string = decodeURIComponent(redirectAttrs.code);

    return { state: parsedState, code: authCode };
};

type SSOSaga = Generator<
    CallEffect<LoginResponse> | CallEffect<TAndCResponse> | PutEffect<GetUserConsentForClient> | RequestActions,
    void,
    LoginResponse & TAndCResponse
>;

export function* ssoLoginSaga(): SSOSaga {
    const searchQuery: SsoQueryParams = getSearchQueryFromState();
    window.history.replaceState({}, '', `${window.location.pathname}/${searchQuery.state.urlQueryParams}`);
    try {
        const response = yield call(tokenExchange, {
            clientId: searchQuery.state.clientId,
            authCode: searchQuery.code,
        });
        const now = dayjs().subtract(5, 'minutes').unix();
        const {
            expires_in: expiresIn,
            refresh_token: refreshToken,
            id_token: idToken,
            access_token: accessToken,
        } = response;
        const expiresAt = now + parseInt(expiresIn, 10);

        const parsedResponse = {
            refreshToken,
            accessToken,
            idToken,
            expiresAt: expiresAt.toString(),
        };

        updateTokenStorage(parsedResponse);
        const termsResponse = yield call(checkTermsAndConditions);
        if (!termsResponse.isAgreed) {
            redirectWithQuery('/terms-and-conditions');
        } else {
            const willRedirect = shouldRedirectToClient(searchQuery.state.urlQueryParams);
            if (willRedirect) {
                yield put(getUserConsentForClient());
            } else {
                redirectWithQuery('/');
            }
        }
        yield put(requestSuccess(RequestType.ExchangeSsoToken, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.ExchangeSsoToken, RequestActionType.Error));
    }
}

type SSOTermsSaga = Generator<CallEffect | PutEffect<GetUserConsentForClient> | RequestActions, void>;

export function* ssoTermsSaga(): SSOTermsSaga {
    const searchQuery = window.location.search;
    try {
        yield call(setTermsAndConditions);
        const willRedirect = shouldRedirectToClient(searchQuery);
        if (willRedirect) {
            yield put(getUserConsentForClient());
        } else {
            redirectWithQuery('/');
        }
        yield put(requestSuccess(RequestType.SetTermsAndConditions, RequestActionType.Success));
    } catch (error) {
        const errorAsErrorType = toErrorType(error);
        yield put(requestError(errorAsErrorType, RequestType.SetTermsAndConditions, RequestActionType.Error));
    }
}

export default function* (): Generator {
    yield takeEvery(LoginAndRegisterActionType.ExchangeSsoTokenInit, ssoLoginSaga);
    yield takeEvery(ConsentActionType.SetTermsAndConditionsInit, ssoTermsSaga);
}
